import JSONbig from "json-bigint";
import { HttpService } from "../../common/api/httpService";
import { RequestResultValue } from "../../common/api/requestResult";
import { PaginationResponse } from "../../models/paginationResponse";
import {
    AdminCriticalActionPreview,
    AdminExternalAccountInfo,
    AdminExternalAuditInfo,
    AdminExternalProfitRuleInfo,
    AdminExternalProviderConnection,
    AdminExternalProviderInfo,
    AdminExternalPurchaseInfo,
    BindExternalSteamOfferRequest,
    CreateExternalAccountRequest,
    ExtAnalyticsParams,
    ExtFailureRow,
    ExternalAccountCheckResult,
    ExternalProxiesSummary,
    ExternalPurchasesFilter,
    ExtProviderRow,
    ExtShareRow,
    ExtTimeseriesPoint,
    ExtTopItem,
    ResolveExternalPurchaseReviewRequest,
    SaveExternalProfitRuleRequest,
    UpdateExternalAccountRequest,
    UpdateExternalMarketRequest,
} from "../../models/externalMarkets";

/** Большие числа (SteamId) — строками, без потери точности */
const JSONbigSafe = JSONbig({ storeAsString: true });

/**
 * Результат вызова: при ошибке — текст из ответа API (400 с сообщением сервиса) или общий текст
 */
export class ApiResult<T> extends RequestResultValue<T> {
    public error?: string;

    constructor(res: boolean, v?: T, error?: string) {
        super(res, v);
        this.error = error;
    }
}

function readError(body: any): string | undefined {
    if (!body || typeof body !== "object") {
        return undefined;
    }
    return body.Message ?? body.message ?? body.error?.description ?? body.title;
}

async function send<T>(method: "GET" | "POST" | "PUT" | "DELETE", path: string, data?: object): Promise<ApiResult<T>> {
    const http = HttpService.getInstance();
    const json = data ? JSONbig.stringify(data) : "";
    let response: Response;
    try {
        switch (method) {
            case "GET":
                response = await http.requestGetAsync(path);
                break;
            case "POST":
                response = await http.requestPostAsync(path, json);
                break;
            case "PUT":
                response = await http.requestPutAsync(path, json);
                break;
            default:
                response = await http.requestDeleteAsync(path);
                break;
        }
    } catch {
        return new ApiResult<T>(false, undefined, "Сервер недоступен");
    }

    const text = await response.text();
    let body: any = undefined;
    if (text) {
        try {
            body = JSONbigSafe.parse(text);
        } catch {
            body = undefined;
        }
    }

    if (response.status >= 200 && response.status < 300) {
        return new ApiResult<T>(true, (body ?? true) as T);
    }
    return new ApiResult<T>(false, undefined, readError(body) ?? `Ошибка ${response.status}`);
}

const base = "admin/ExternalMarkets";
const provider = (key: string) => `${base}/${encodeURIComponent(key)}`;
const market = (key: string, gameId: number) => `${provider(key)}/games/${gameId}`;

// ---------- Поставщики и связки ----------

export const extProvidersApi = () => send<AdminExternalProviderInfo[]>("GET", base);

export const extProviderToggleApi = (key: string, enable: boolean, reason?: string) =>
    send<boolean>("POST", `${provider(key)}/${enable ? "enable" : "disable"}`, { reason });

export const extMarketToggleApi = (key: string, gameId: number, enable: boolean, reason?: string) =>
    send<boolean>("POST", `${market(key, gameId)}/${enable ? "enable" : "disable"}`, { reason });

export const extMarketUpdateApi = (key: string, gameId: number, request: UpdateExternalMarketRequest) =>
    send<boolean>("PUT", market(key, gameId), request);

export const extMarketSyncApi = (key: string, gameId: number) =>
    send<boolean>("POST", `${market(key, gameId)}/sync`);

export const extProviderSettingsApi = (key: string, partnerBotSteamIds: string[]) =>
    send<boolean>("PUT", `${provider(key)}/settings`, {
        // ulong на сервере: отправляем числом без потери точности
        partnerBotSteamIds: partnerBotSteamIds.map((id) => BigInt(id)),
    });

export const extSystemUserNameApi = (key: string, name: string) =>
    send<boolean>("PUT", `${provider(key)}/system-user/name`, { name });

export const extProviderConnectionApi = (key: string, connection: AdminExternalProviderConnection) =>
    send<boolean>("PUT", `${provider(key)}/connection`, connection);

// ---------- Правила прибыли ----------

export const extProfitRulesApi = (key: string, gameId: number) =>
    send<AdminExternalProfitRuleInfo[]>("GET", `${market(key, gameId)}/profit-rules`);

export const extProfitRuleSaveApi = (key: string, gameId: number, request: SaveExternalProfitRuleRequest) =>
    send<AdminExternalProfitRuleInfo>("PUT", `${market(key, gameId)}/profit-rules`, request);

export const extProfitRuleDeleteApi = (key: string, gameId: number, ruleId: number) =>
    send<boolean>("DELETE", `${market(key, gameId)}/profit-rules/${ruleId}`);

// ---------- Выкупы и журнал ----------

export const extPurchasesApi = (key: string, filter: ExternalPurchasesFilter) =>
    send<PaginationResponse<AdminExternalPurchaseInfo>>("POST", `${provider(key)}/purchases`, {
        gameId: filter.gameId,
        accountId: filter.accountId,
        statuses: filter.statuses && filter.statuses.length > 0 ? filter.statuses : undefined,
        from: filter.from?.toISOString(),
        to: filter.to?.toISOString(),
        needsReview: filter.needsReview || undefined,
        page: filter.page,
        count: filter.count,
    });

// ---------- Ручной разбор выкупа ----------

export const extPurchaseReviewCountApi = (key: string) =>
    send<number>("GET", `${provider(key)}/purchases/review-count`);

/** Только читает статусы трейдов у поставщика; итог допишется в разбор через несколько секунд */
export const extPurchaseRefreshApi = (key: string, purchaseId: number) =>
    send<boolean>("POST", `${provider(key)}/purchases/${purchaseId}/refresh`);

/** Критическое действие: без confirm — предпросмотр, с confirm и причиной — выполнение */
export const extPurchaseBindSteamOfferApi = (key: string, purchaseId: number, request: BindExternalSteamOfferRequest) =>
    send<AdminCriticalActionPreview>("POST", `${provider(key)}/purchases/${purchaseId}/bind-steam-offer`, {
        ...request,
        // long на сервере: отправляем числом без потери точности
        steamOfferId: BigInt(request.steamOfferId),
    });

/** Критическое действие: без confirm — предпросмотр, с confirm и причиной — выполнение */
export const extPurchaseResolveApi = (key: string, purchaseId: number, request: ResolveExternalPurchaseReviewRequest) =>
    send<AdminCriticalActionPreview>("POST", `${provider(key)}/purchases/${purchaseId}/resolve`, request);

export const extAuditApi = (key: string, page: number, count: number) =>
    send<PaginationResponse<AdminExternalAuditInfo>>("GET", `${provider(key)}/audit?page=${page}&count=${count}`);

// ---------- Аккаунты и прокси ----------

export const extAccountsApi = (key: string) =>
    send<AdminExternalAccountInfo[]>("GET", `${provider(key)}/accounts`);

export const extAccountCreateApi = (key: string, request: CreateExternalAccountRequest) =>
    send<AdminExternalAccountInfo>("POST", `${provider(key)}/accounts`, request);

export const extAccountUpdateApi = (key: string, accountId: number, request: UpdateExternalAccountRequest) =>
    send<boolean>("PUT", `${provider(key)}/accounts/${accountId}`, {
        ...request,
        apiKey: request.apiKey && request.apiKey.trim() ? request.apiKey.trim() : undefined,
    });

export const extAccountDeleteApi = (key: string, accountId: number) =>
    send<boolean>("DELETE", `${provider(key)}/accounts/${accountId}`);

export const extAccountCheckApi = (key: string, accountId: number) =>
    send<ExternalAccountCheckResult>("POST", `${provider(key)}/accounts/${accountId}/check`);

export const extAccountResetCooldownApi = (key: string, accountId: number) =>
    send<boolean>("POST", `${provider(key)}/accounts/${accountId}/reset-cooldown`);

export const extAccountRotateProxyApi = (key: string, accountId: number) =>
    send<boolean>("POST", `${provider(key)}/accounts/${accountId}/rotate-proxy`);

export const extProxiesSummaryApi = (key: string) =>
    send<ExternalProxiesSummary>("GET", `${provider(key)}/proxies/summary`);

// ---------- Аналитика ----------

const analyticsBase = "admin/ExternalMarketAnalytics";

function analyticsQuery(p: ExtAnalyticsParams, extra: Record<string, string | number | undefined> = {}): string {
    const q = new URLSearchParams();
    q.set("from", p.from.toISOString());
    q.set("to", p.to.toISOString());
    Object.entries(extra).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") {
            q.set(k, String(v));
        }
    });
    return q.toString();
}

export const extSummaryApi = (p: ExtAnalyticsParams) =>
    send<ExtShareRow[]>("GET", `${analyticsBase}/summary?${analyticsQuery(p)}`);

export const extByProviderApi = (p: ExtAnalyticsParams) =>
    send<ExtProviderRow[]>("GET", `${analyticsBase}/by-provider?${analyticsQuery(p, { gameId: p.gameId })}`);

export const extTimeseriesApi = (p: ExtAnalyticsParams) =>
    send<ExtTimeseriesPoint[]>("GET", `${analyticsBase}/timeseries?${analyticsQuery(p, {
        granularity: p.granularity,
        gameId: p.gameId,
        providerKey: p.providerKey,
    })}`);

export const extFailuresApi = (p: ExtAnalyticsParams) =>
    send<ExtFailureRow[]>("GET", `${analyticsBase}/failures?${analyticsQuery(p, { providerKey: p.providerKey })}`);

export const extTopItemsApi = (p: ExtAnalyticsParams) =>
    send<ExtTopItem[]>("GET", `${analyticsBase}/top-items?${analyticsQuery(p, {
        providerKey: p.providerKey,
        take: p.take,
    })}`);
